import React from 'react';

interface PlayheadTimelineProps {
  elapsedSeconds: number;
  expectedDurationMinutes: number | null;
  maxDurationMinutes?: number;
  isPaused?: boolean;
}

const PlayheadTimeline: React.FC<PlayheadTimelineProps> = ({
  elapsedSeconds,
  expectedDurationMinutes,
  maxDurationMinutes = 60,
  isPaused = false,
}) => {
  const maxDurationSeconds = maxDurationMinutes * 60;
  const playheadPosition = Math.min((elapsedSeconds / maxDurationSeconds) * 100, 100);
  
  const expectedPosition = expectedDurationMinutes 
    ? Math.min((expectedDurationMinutes * 60 / maxDurationSeconds) * 100, 100)
    : null;

  return (
    <div className="w-full pt-2 pb-8">
      <div className="relative w-full h-1.5 bg-surface-skeleton rounded-full">
        {/* Progress Fill */}
        <div 
          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
            isPaused ? 'bg-gray-400' : 'bg-primary-600'
          }`}
          style={{ width: `${playheadPosition}%` }}
        />

        {/* Improved Expected Marker */}
        {expectedPosition !== null && (
          <div 
            className="absolute top-0 flex flex-col items-center -translate-x-1/2" 
            style={{ left: `${expectedPosition}%` }}
          >
            {/* The Notch */}
            <div className="w-0.5 h-3 bg-gray-400 mt-[-3px] z-10" />
            
            {/* The Clear Label */}
            <div className="mt-2 flex flex-col items-center">
              <span className="text-[10px] font-black text-muted uppercase tracking-tighter leading-none">
                Expected
              </span>
              <span className="text-[12px] font-bold text-heading bg-surface-subtle px-1.5 py-0.5 rounded mt-0.5">
                {expectedDurationMinutes}m
              </span>
            </div>
          </div>
        )}

        {/* Playhead Dot */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white shadow-md transition-all duration-300 z-20 ${
            isPaused ? 'bg-surface-base0 scale-90' : 'bg-primary-600 scale-100'
          }`}
          style={{ left: `${playheadPosition}%` }}
        />

        {/* Track Ends Labels */}
        <div className="absolute -bottom-7 left-0 text-[10px] font-bold text-disabled uppercase tracking-widest">0m</div>
        <div className="absolute -bottom-7 right-0 text-[10px] font-bold text-disabled uppercase tracking-widest">Ongoing</div>
      </div>
    </div>
  );
};

export default PlayheadTimeline;